import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import ProductMain from "../layouts/main/ProductMain";
import { ProductContext } from "../context/ProductContext";
import Header from "../layouts/header/Header";
import {
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
  getPriceByQuantity,
  transportMethods,
} from "../utils/global_const";
import descriptions from "../descriptions.json";
import { useParams } from "react-router-dom";

const Container = styled.div`
  background-color: var(--background-color);
  color: var(--text-color);
`;

const Product = () => {
  const { t, i18n } = useTranslation();
  const { product } = useContext(ProductContext);
  const { lang: langParam, category, slug } = useParams();

  useEffect(() => {
    if (
      langParam &&
      (langParam === "de" || langParam === "en" || langParam === "hu") &&
      i18n.language?.split("-")[0] !== langParam
    ) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  const [currencyCode, setCurrencyCode] = useState(
    localStorage.getItem("currency") || DEFAULT_CURRENCY
  );

  // Generiši canonical URL na snuswe.com
  const productUrl = product
    ? `https://www.snuswe.com/${langParam || i18n.language}/${category}/${slug}`
    : "https://www.snuswe.com";

  // URL iste stranice na drugom jeziku — isti category/slug u svim jezicima (App.jsx: /:lang/:category/:slug)
  const productUrlForLang = (langCode) =>
    `https://www.snuswe.com/${langCode}/${category}/${slug}`;

  // Odredi jezik za i18n
  const lang = i18n.language.startsWith("de")
    ? "de"
    : i18n.language.startsWith("hu")
    ? "hu"
    : "en";
  const shortKey = `short_${lang}`;
  // Pronađi opis za trenutni proizvod
  const descItem = descriptions.find((d) => d.slug === slug) || {};
  const metaDescription = descItem[shortKey] || "";

  // Napravi keywords polje na osnovu atributa
  const keywords = [
    product?.category?.name,
    product?.name,
    product?.flavor,
    `${product?.nicotine}mg`,
    product?.format,
    "nicotine pouches",
  ]
    .filter(Boolean)
    .join(", ");

  // OG/Twitter slika
  const ogImageObj =
    product?.images.find((img) => img.is_primary) || product?.images[0] || null;
  const ogImage =
    ogImageObj?.large || ogImageObj?.original || ogImageObj?.thumbnail || null;

  return (
    <Container>
      <Helmet>
        <html lang={i18n.language} />
        {/* Title */}
        <title>
          {product?.name
            ? `${product.name} | Nicotine Pouches SnusWe`
            : "Nicotine Pouches SnusWe"}
        </title>

        {/* Meta Description & Keywords */}
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:title" content={product?.name} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={ogImage} />

        {/* Canonical & Hreflang — pun set: svaka jezička verzija + x-default */}
        <link rel="canonical" href={productUrl} />
        {category &&
          slug &&
          ["en", "de", "hu"].map((langCode) => (
            <link
              key={langCode}
              rel="alternate"
              hreflang={langCode}
              href={productUrlForLang(langCode)}
            />
          ))}
        {category && slug && (
          <link
            rel="alternate"
            hreflang="x-default"
            href={productUrlForLang(DEFAULT_LANGUAGE)}
          />
        )}

        {/* JSON-LD za Product */}
        {product && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: product.name,
              description: metaDescription,
              sku: product.sku,
              brand: { "@type": "Brand", name: product.category?.name },
              image: ogImage,
              offers: {
                "@type": "Offer",
                priceCurrency: currencyCode,
                price: product.price,
                itemCondition: "https://schema.org/NewCondition",
                availability:
                  product.is_in_stock === "in_stock"
                    ? "https://schema.org/InStock"
                    : product.is_in_stock === "on_request"
                    ? "https://schema.org/BackOrder"
                    : "https://schema.org/OutOfStock",
                url: productUrl,
                shippingDetails: {
                  "@type": "OfferShippingDetails",
                  shippingRate: {
                    "@type": "MonetaryAmount",
                    value: transportMethods[0].price,
                    currency: currencyCode,
                  },
                  shippingDestination: {
                    "@type": "DefinedRegion",
                    addressCountry: "AT",
                  },
                  shippingMethod: transportMethods[0].name,
                },
              },
            })}
          </script>
        )}

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={product?.name} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <Header />
      <ProductMain />
    </Container>
  );
};

export default Product;
